/**
 * What a keypress means in a room. Kept as a pure function of the key and the
 * state around it so the rules — who may reveal, when a card key does nothing,
 * what happens while someone is typing a story title — are testable without a
 * DOM. The hook that listens is a thin wrapper over this.
 */

export type ShortcutAction =
  | { readonly type: 'pick'; readonly index: number }
  | { readonly type: 'retract' }
  | { readonly type: 'reveal' }
  | { readonly type: 'nextStory' };

export interface ShortcutContext {
  readonly isPlayer: boolean;
  readonly isHost: boolean;
  readonly revealed: boolean;
  /** Whether there is anything on the table worth turning over. */
  readonly anyVotes: boolean;
  /** Focus is in a field, so the keystroke belongs to that field. */
  readonly typing: boolean;
  /** A modifier is held, so this is a browser or OS shortcut, not ours. */
  readonly modified: boolean;
  readonly deckSize: number;
}

/**
 * Cards are addressed by position rather than by face value: in Fibonacci the
 * card printed `1` is the second card, and a key that plays a different number
 * from the one on it would be worse than no shortcut at all. `0` reaches the
 * tenth card so the first ten of any deck are one press away.
 */
function cardIndexFor(key: string): number | null {
  if (key.length !== 1 || key < '0' || key > '9') return null;
  return key === '0' ? 9 : Number(key) - 1;
}

export function shortcutFor(key: string, context: ShortcutContext): ShortcutAction | null {
  if (context.typing || context.modified) return null;

  const index = cardIndexFor(key);
  if (index !== null) {
    if (!context.isPlayer || context.revealed || index >= context.deckSize) return null;
    return { type: 'pick', index };
  }

  if (key === 'Backspace' || key === 'Delete') {
    if (!context.isPlayer || context.revealed) return null;
    return { type: 'retract' };
  }

  const lower = key.toLowerCase();

  if (lower === 'r') {
    if (!context.isHost || context.revealed || !context.anyVotes) return null;
    return { type: 'reveal' };
  }

  if (lower === 'n') {
    if (!context.isHost || !context.revealed) return null;
    return { type: 'nextStory' };
  }

  return null;
}
